import { supabase } from './supabase';
import { logger } from './logger';

export type PopulationDim = 'age_band' | 'gender' | 'department' | 'sector' | 'position' | 'contract';

export interface PopulationCell {
  x: string;
  color: string;
  facet: string | null;
  n: number;
}

export interface PopulationBreakdown {
  total: number;
  cells: PopulationCell[];
}

export async function fetchPopulationBreakdown(params: {
  role: 'candidate' | 'employer';
  dimX: PopulationDim;
  dimColor: PopulationDim;
  dimFacet?: PopulationDim | null;
}): Promise<PopulationBreakdown> {
  const { data, error } = await supabase.rpc('get_population_breakdown', {
    p_role: params.role,
    p_dim_x: params.dimX,
    p_dim_color: params.dimColor,
    p_dim_facet: params.dimFacet || null,
  });
  if (error) {
    logger.error('get_population_breakdown', 'populationService', { error: error.message });
    return { total: 0, cells: [] };
  }
  const raw = data as { total?: number; cells?: PopulationCell[] };
  return {
    total: raw?.total || 0,
    cells: Array.isArray(raw?.cells) ? raw.cells : [],
  };
}

export async function estimateAudience(params: {
  sectorCode?: string;
  ageBand?: string;
  department?: string;
}): Promise<{ bucket: number; underMin: boolean }> {
  const { data, error } = await supabase.rpc('estimate_audience', {
    p_sector_code: params.sectorCode || null,
    p_age_band: params.ageBand || null,
    p_department: params.department || null,
  });
  if (error) {
    logger.error('estimate_audience', 'populationService', { error: error.message });
    throw new Error(error.message);
  }
  const raw = data as { bucket?: number; under_min?: boolean };
  return {
    bucket: raw?.bucket ?? 0,
    underMin: !!raw?.under_min,
  };
}

export function buildInsight(cells: PopulationCell[], total: number): string {
  if (!cells.length || total <= 0) return 'Pas assez de profils pour un croisement.';
  const top = [...cells].sort((a, b) => b.n - a.n)[0];
  const pct = Math.round((top.n / total) * 100);
  const parts = [top.x, top.color, top.facet].filter((v) => v && v !== 'unknown');
  return `Croisement le plus dense : ${parts.join(' × ')} (${top.n} profils, ${pct} %).`;
}

const DIM_LABELS: Record<PopulationDim, string> = {
  age_band: 'Tranche d’âge',
  gender: 'Genre',
  department: 'Département',
  sector: 'Secteur',
  position: 'Poste',
  contract: 'Contrat',
};

export function dimLabel(dim: PopulationDim): string {
  return DIM_LABELS[dim];
}
